'use client';

/**
 * Horizontally-scrollable hero with snap points. Ported from
 * ThemeMaker's {@code blocks/hero/HeroSlider} — simplified to drop
 * the {@code useBtkBanners} fetch hook (BTK-private; hosts that need
 * dynamic banners can either compose a wrapper that fetches and passes
 * {@code slides}, or register their own variant under a different
 * block id).
 *
 * <h3>Slide types</h3>
 * <ul>
 *   <li><b>Image-only</b> — slide has {@code imageUrl} but no
 *       {@code title}. Renders as a clickable banner with 11:4
 *       aspect ratio.</li>
 *   <li><b>Text overlay</b> — slide has {@code title}. Renders a
 *       card with kicker/title/body/CTA on a colour or image-with-
 *       gradient background.</li>
 * </ul>
 *
 * <h3>Prev/next arrows + dots</h3>
 * Arrows are desktop-only (hidden below 768px, where swipe/native
 * scroll is the primary gesture). Both arrows and dots move exactly
 * one slide per click/tap — the target slide is centered in the rail
 * by measuring its real {@code offsetLeft}/{@code offsetWidth} rather
 * than guessing from container width, so it always lands fully
 * visible instead of overshooting past it. A scroll listener derives
 * the nearest slide from scroll position so the dots (and arrow
 * enabled/disabled state) also stay in sync with manual swipe/scroll,
 * not just clicks.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { tx, asList } from '../_shared/coerce';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  useBlockPart,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import { Button } from '@akademi/ui';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroSliderSlide extends CampaignAudience {
  kicker?: Localized;
  title?: Localized;
  body?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  imageUrl?: string;
  /** Accent picks the background gradient when no image is set:
   *  {@code 'ink'} (indigo), {@code 'warm'} (amber), default brand. */
  accent?: 'ink' | 'warm' | string;
}

export interface HeroSliderProps {
  slides?: HeroSliderSlide[];
  /** 'full' (default) lets the rail span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function HeroSlider({ slides, maxWidth = 'full' }: HeroSliderProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const currentUser = useCurrentUser<CurrentUser>();
  const effective = useMemo(
    () =>
      asList<HeroSliderSlide>(slides)
        // Drop slides with no actual content to render (same convention
        // as HeroCarousel) — otherwise a contentless slide survives as a
        // blank colored card instead of ever hitting the branded
        // HeroEmptyFallback below, and an all-empty deck never collapses
        // to the single full-width fallback either.
        .filter((s) => s && (s.title || s.kicker || s.body || s.ctaLabel || s.imageUrl))
        .filter((s) => isVisible(s, currentUser)),
    [slides, currentUser],
  );

  const railRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const canPrev = activeIndex > 0;
  const canNext = activeIndex < effective.length - 1;

  // Finds whichever slide's centre sits closest to the rail's current
  // centre — used to keep the dots + arrow enabled-state in sync with
  // manual swipe/scroll/keyboard, not just our own goTo() calls.
  const syncActiveFromScroll = () => {
    const rail = railRef.current;
    if (!rail) return;
    const railCenter = rail.scrollLeft + rail.clientWidth / 2;
    let closest = 0;
    let closestDist = Infinity;
    slideRefs.current.forEach((el, i) => {
      if (!el) return;
      const center = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(center - railCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    setActiveIndex(closest);
  };

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    syncActiveFromScroll();
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        syncActiveFromScroll();
      });
    };
    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', syncActiveFromScroll);
    return () => {
      rail.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', syncActiveFromScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [effective.length]);

  // Straddles the theme max-width edge (half the 44px arrow overlapping the
  // rail, half hanging outside it); falls back to the plain edge inset once
  // the viewport is narrower than the theme max-width and there's no edge
  // to straddle.
  const themeArrowOffset =
    'max(var(--space-2, 8px), calc(50% - min(var(--sb-max-width-1200, var(--app-max-width, 1200px)), 100%) / 2 - 22px))';

  const goTo = (i: number) => {
    const rail = railRef.current;
    const target = slideRefs.current[i];
    if (!rail || !target) return;
    rail.scrollTo({
      left: target.offsetLeft - (rail.clientWidth - target.offsetWidth) / 2,
      behavior: 'smooth',
    });
    setActiveIndex(i);
  };

  if (effective.length === 0) {
    return (
      <BlockRoot
        as="section"
        style={{ padding: 'clamp(var(--space-5, 20px), 4vw, var(--space-8, 32px)) 0' }}
      >
        <HeroEmptyFallback
          style={{
            width: 'min(1100px, 90%)',
            margin: '0 auto',
            minHeight: 'clamp(220px, 40vw, 320px)',
            borderRadius: 'var(--radius-lg, 16px)',
          }}
        />
      </BlockRoot>
    );
  }

  return (
    <BlockRoot
      as="section"
      style={{ padding: 'clamp(var(--space-5, 20px), 4vw, var(--space-8, 32px)) 0' }}
    >
      <div style={{ position: 'relative' }}>
        <div
          ref={railRef}
          className="hero-slider-rail"
          style={{
            display: 'flex',
            gap: 'clamp(var(--space-2-5, 10px), 2vw, var(--space-4, 16px))',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            padding: '0 clamp(var(--space-3-5, 14px), 2.5vw, var(--space-6, 24px))',
            scrollbarWidth: 'none',
            overscrollBehaviorX: 'contain',
            ...(maxWidth === 'theme'
              ? {
                  maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
                  width: '100%',
                  margin: '0 auto',
                }
              : {}),
          }}
        >
          {effective.map((s, i) => {
            // Image-only slide (banner reel pattern).
            if (s.imageUrl && !s.title) {
              return (
                <PortalLink
                  key={i}
                  ref={(el: HTMLAnchorElement | null) => {
                    slideRefs.current[i] = el;
                  }}
                  href={s.ctaHref ?? '#'}
                  className="hero-slider-banner"
                  style={{
                    flex: '0 0 min(1100px, 90%)',
                    scrollSnapAlign: 'center',
                    borderRadius: 'var(--radius-lg, 16px)',
                    overflow: 'hidden',
                    display: 'block',
                    aspectRatio: '11 / 4',
                    backgroundImage: `url("${s.imageUrl}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'var(--color-neutral-100, #f4f4f5)',
                  }}
                  aria-label={t('hero.slider.banner', { index: i + 1 })}
                />
              );
            }
            // Text overlay slide.
            return (
              <article
                key={i}
                ref={(el) => {
                  slideRefs.current[i] = el;
                }}
                className="hero-slider-article"
                style={{
                  flex: '0 0 min(900px, 90%)',
                  scrollSnapAlign: 'center',
                  minHeight: 'clamp(220px, 40vw, 320px)',
                  borderRadius: 'var(--radius-lg, 16px)',
                  padding: 'clamp(var(--space-5, 20px), 4vw, var(--space-14, 56px))',
                  color: 'var(--color-text-on-brand, #fff)',
                  background: s.imageUrl
                    ? `linear-gradient(135deg, rgba(0,0,0,0.55), rgba(0,0,0,0.25)), url("${s.imageUrl}") center/cover`
                    : accentBg(s.accent),
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                {s.kicker && (
                  <div
                    {...part('kicker', i)}
                    style={{
                      fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                      fontSize: 'clamp(10px, 1.2vw, 11px)',
                      letterSpacing: 'var(--font-tracking-widest, 0.08em)',
                      textTransform: 'uppercase',
                      opacity: 0.75,
                      marginBottom: 'var(--space-3, 12px)',
                    }}
                  >
                    {tx(s.kicker)}
                  </div>
                )}
                {s.title && (
                  <h2
                    {...part('title', i)}
                    style={{
                      margin: 0,
                      fontSize: 'clamp(22px, 4vw, 44px)',
                      fontWeight: 'var(--font-weight-semibold, 600)',
                      letterSpacing: 'var(--font-tracking-snug, -0.01em)',
                      fontFamily: 'var(--font-family-display, inherit)',
                      maxWidth: 640,
                      lineHeight: 'var(--line-height-tight, 1.15)',
                    }}
                  >
                    {tx(s.title)}
                  </h2>
                )}
                {s.body && (
                  <p
                    {...part('subtitle', i)}
                    style={{
                      margin: 'var(--space-3, 12px) 0 0',
                      maxWidth: 540,
                      fontSize: 'clamp(14px, 1.8vw, 16px)',
                      opacity: 0.85,
                      lineHeight: 'var(--line-height-relaxed, 1.5)',
                    }}
                  >
                    {tx(s.body)}
                  </p>
                )}
                {s.ctaLabel && (
                  <Button
                    href={s.ctaHref ?? '#'}
                    {...part('cta', i)}
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: 'var(--space-6, 24px)',
                      height: 'auto',
                      padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
                      background: 'rgba(255,255,255,0.95)',
                      color: 'var(--color-text-primary, #18181b)',
                    }}
                  >
                    {tx(s.ctaLabel)}
                  </Button>
                )}
              </article>
            );
          })}
        </div>

        {effective.length > 1 && (
          <>
            <button
              type="button"
              aria-label={t('hero.carousel.previous')}
              className="hero-slider-arrow hero-slider-arrow--prev"
              disabled={!canPrev}
              onClick={() => goTo(activeIndex - 1)}
              {...part('pager', 0)}
              style={{
                opacity: canPrev ? 1 : 0,
                pointerEvents: canPrev ? 'auto' : 'none',
                ...(maxWidth === 'theme' ? { left: themeArrowOffset } : {}),
              }}
            >
              <ChevronLeft size={20} aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t('hero.carousel.next')}
              className="hero-slider-arrow hero-slider-arrow--next"
              disabled={!canNext}
              onClick={() => goTo(activeIndex + 1)}
              {...part('pager', 1)}
              style={{
                opacity: canNext ? 1 : 0,
                pointerEvents: canNext ? 'auto' : 'none',
                ...(maxWidth === 'theme' ? { right: themeArrowOffset } : {}),
              }}
            >
              <ChevronRight size={20} aria-hidden />
            </button>
          </>
        )}
      </div>

      {effective.length > 1 && (
        <div
          {...part('tablist')}
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--space-2, 8px)',
            marginTop: 'var(--space-4, 16px)',
          }}
        >
          {effective.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={t('hero.carousel.goToSlide', { index: i + 1 })}
              aria-current={i === activeIndex || undefined}
              onClick={() => goTo(i)}
              {...part('tab', i)}
              style={{
                // Hit area meets the 24x24 CSS px touch-target minimum even
                // though the visual dot (below) stays small.
                width: 24,
                height: 24,
                padding: 0,
                border: 'none',
                background: 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background:
                    i === activeIndex
                      ? 'var(--color-text-primary, #18181b)'
                      : 'var(--color-neutral-300, #d4d4d8)',
                  transition: 'background 150ms ease',
                }}
              />
            </button>
          ))}
        </div>
      )}

      {/* Image-only banners use a wide 11:4 ratio at desktop widths;
       *  on narrow viewports that crops down to a sliver, so switch
       *  to a taller 16:9 box instead. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .hero-slider-rail::-webkit-scrollbar { display: none; }
        @media (max-width: 640px) {
          .hero-slider-banner { aspect-ratio: 16 / 9; }
        }

        .hero-slider-arrow {
          display: none;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          padding: 0;
          box-sizing: border-box;
          border-radius: 50%;
          border: 1px solid var(--color-border-default, rgba(0,0,0,0.12));
          background: var(--color-surface-card, #fff);
          color: var(--color-text-primary, #18181b);
          box-shadow: var(--shadow-md, 0 6px 16px rgba(15,18,24,0.12));
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
          transition: opacity 150ms ease, background 150ms ease, transform 150ms ease;
        }
        .hero-slider-arrow:hover:not(:disabled) {
          background: var(--color-surface-muted, #f4f4f5);
        }
        .hero-slider-arrow:disabled {
          cursor: default;
        }
        .hero-slider-arrow--prev { left: var(--space-2, 8px); }
        .hero-slider-arrow--next { right: var(--space-2, 8px); }

        @media (min-width: 768px) {
          .hero-slider-arrow { display: inline-flex; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}

function accentBg(a?: string): string {
  return a === 'ink'
    ? 'linear-gradient(135deg, #1e1b4b, #312e81)'
    : a === 'warm'
      ? 'linear-gradient(135deg, #b45309, #f59e0b)'
      : 'linear-gradient(135deg, var(--color-brand-600, #2563eb), var(--color-brand-500, #3b82f6))';
}
